import { Stack } from '@mantine/core';
import { VLazyLoadMultiSelect, VLazyLoadSelect, VSelect, VSelectOptionWithDescription } from '@vision/ui/components';
import { ChannelType, SchemaType } from '@vision/ui/enums';
import { useChannelTypes, useSelectedAccountId } from '@vision/ui/hooks';
import type {
  ReportsDetailGraphicSchemaValues,
  SchemaField,
  SimpleChannel,
  TableDispatchSchemaSelectionValue,
} from '@vision/ui/interfaces';
import {
  useLazyFetchChannelsWithPaginationQuery,
  useLazyFetchFlowsWithPaginationQuery,
  useLazyGetSchemaFieldsV2Query,
} from '@vision/ui/services';
import { translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableDispatchSchemaValueSelect } from './TableDispatchSchemaValueSelect';

const TABLE_DISPATCH_CHANNEL_TYPES = [
  ChannelType.LINK,
  ChannelType.EMAIL,
  ChannelType.SMS,
  ChannelType.PUSH_NOTIFICATION,
  ChannelType.WHATSAPP,
];

const MAX_SCHEMA_SELECTIONS = 10;

export function TableDispatchValue() {
  const { t } = useTranslation(['translation', 'page-reports']);
  const accountId = useSelectedAccountId();
  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();
  const channelTypeSelectItems = useChannelTypes(TABLE_DISPATCH_CHANNEL_TYPES);
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const tableDispatch = formik.values.selectedTableDispatch;
  const selectedSchemaFieldIds = tableDispatch.schemaSelections.map(({ fieldId }) => fieldId);

  const setTableDispatch = (value: ReportsDetailGraphicSchemaValues['selectedTableDispatch']) => {
    formik.setFieldValue('selectedTableDispatch', value);
  };

  const handleChannelTypeChange = (value: string | null) => {
    setTableDispatch({
      channelId: null,
      channelType: value as ChannelType | null,
      flowId: null,
      schemaSelections: [],
    });
  };

  const handleChannelChange = (value: string | null) => {
    setTableDispatch({
      ...tableDispatch,
      channelId: value,
      flowId: null,
      schemaSelections: [],
    });
  };

  const handleFlowChange = (value: string | null) => {
    setTableDispatch({
      ...tableDispatch,
      flowId: value,
      schemaSelections: [],
    });
  };

  const updateSchemaSelections = (fieldIds: string[], availableFields = schemaFields) => {
    const currentSelections = new Map(
      tableDispatch.schemaSelections.map((selection) => [selection.fieldId, selection]),
    );
    const fieldsById = new Map(availableFields.map((field) => [field.id, field]));
    const schemaSelections = fieldIds.flatMap<TableDispatchSchemaSelectionValue>((fieldId) => {
      const currentSelection = currentSelections.get(fieldId);
      const field = fieldsById.get(fieldId);

      if (currentSelection) {
        return [
          field
            ? {
                ...currentSelection,
                fieldCode: field.code,
                fieldName: field.name,
              }
            : currentSelection,
        ];
      }

      return field ? [{ fieldCode: field.code, fieldId: field.id, fieldName: field.name, values: [] }] : [];
    });

    setTableDispatch({ ...tableDispatch, schemaSelections });
  };

  const handleSchemaFieldsLoaded = (fields: SchemaField[]) => {
    const allFields = Array.from(new Map(schemaFields.concat(fields).map((field) => [field.id, field])).values());
    setSchemaFields(allFields);

    const fieldsById = new Map(allFields.map((field) => [field.id, field]));
    const fieldsByCode = new Map(allFields.map((field) => [field.code, field]));
    const schemaSelections = tableDispatch.schemaSelections.map((selection) => {
      const field = fieldsById.get(selection.fieldId) ?? fieldsByCode.get(selection.fieldCode);

      return field
        ? {
            ...selection,
            fieldCode: field.code,
            fieldId: field.id,
            fieldName: field.name,
          }
        : selection;
    });
    const hasChanged = schemaSelections.some((selection, index) => {
      const currentSelection = tableDispatch.schemaSelections[index];

      return (
        selection.fieldCode !== currentSelection.fieldCode ||
        selection.fieldId !== currentSelection.fieldId ||
        selection.fieldName !== currentSelection.fieldName
      );
    });

    if (hasChanged) {
      setTableDispatch({ ...tableDispatch, schemaSelections });
    }
  };

  const handleSchemaValueChange = (fieldId: string, values: string[]) => {
    setTableDispatch({
      ...tableDispatch,
      schemaSelections: tableDispatch.schemaSelections.map((selection) =>
        selection.fieldId === fieldId ? { ...selection, values } : selection,
      ),
    });
  };

  const handleDefaultChannelLoaded = (channels: SimpleChannel[]) => {
    if (tableDispatch.channelId && !channels.some(({ id }) => id === tableDispatch.channelId)) {
      handleChannelChange(null);
    }
  };

  return (
    <Stack gap={10}>
      <VSelect
        id="report-table-dispatch-channel-type"
        data-testid="input-report-table-dispatch-channel-type"
        label={t('channelType')}
        data={channelTypeSelectItems}
        value={tableDispatch.channelType}
        onChange={handleChannelTypeChange}
        error={translateErrorMessage(formik, 'selectedTableDispatch.channelType')}
        withAsterisk={true}
        clearable={true}
      />

      <VLazyLoadSelect
        id="report-table-dispatch-channel"
        data-testid="input-report-table-dispatch-channel"
        label={t('channel')}
        useLazyApiQueryFunction={useLazyFetchChannelsWithPaginationQuery}
        apiRequestParameters={{
          nodeId: accountId,
          types: tableDispatch.channelType ? [tableDispatch.channelType] : [],
          sortBy: 'name',
          sortOrder: 'asc',
        }}
        multiSelectItemMapper={(items) =>
          items.map((item) => ({ value: item.id, label: item.name, description: item.type }))
        }
        searchKey="node_name"
        value={tableDispatch.channelId}
        onChange={handleChannelChange}
        onDefaultValuesLoaded={handleDefaultChannelLoaded}
        error={translateErrorMessage(formik, 'selectedTableDispatch.channelId')}
        disabled={!tableDispatch.channelType}
        withAsterisk={true}
        resetChangeRequestParameters={true}
        itemComponent={VSelectOptionWithDescription}
      />

      <VLazyLoadSelect
        id="report-table-dispatch-flow"
        data-testid="input-report-table-dispatch-flow"
        label={t('flows')}
        useLazyApiQueryFunction={useLazyFetchFlowsWithPaginationQuery}
        apiRequestParameters={{ nodeId: accountId, sortBy: 'name', sortOrder: 'asc' }}
        multiSelectItemMapper={(items) => items.map((item) => ({ value: item.id, label: item.name }))}
        searchKey="flow_name"
        value={tableDispatch.flowId}
        onChange={handleFlowChange}
        error={translateErrorMessage(formik, 'selectedTableDispatch.flowId')}
        disabled={!tableDispatch.channelId}
        withAsterisk={true}
      />

      <VSelect
        id="report-table-dispatch-compare"
        data-testid="input-report-table-dispatch-compare"
        label={t('page-reports:compare')}
        data={[{ value: 'dispatch-schema', label: t('page-reports:tableDispatch.dispatchSchema') }]}
        value="dispatch-schema"
        disabled={true}
        readOnly={true}
        searchable={false}
      />

      {tableDispatch.flowId && (
        <VLazyLoadMultiSelect
          id="report-table-dispatch-distribution-filter"
          data-testid="input-report-table-dispatch-distribution-filter"
          label={t('page-reports:distributionFilter')}
          useLazyApiQueryFunction={useLazyGetSchemaFieldsV2Query}
          apiRequestParameters={{ accountId, schemaType: SchemaType.DISPATCH }}
          multiSelectItemMapper={(items) => items.map((item) => ({ value: item.id, label: item.name }))}
          searchKey="search_string"
          value={selectedSchemaFieldIds}
          onChange={updateSchemaSelections}
          onDataChange={handleSchemaFieldsLoaded}
          onDefaultValuesLoaded={handleSchemaFieldsLoaded}
          error={translateErrorMessage(formik, 'selectedTableDispatch.schemaSelections')}
          withAsterisk={true}
          maxValues={MAX_SCHEMA_SELECTIONS}
          withCloseDropdownMaxValue={true}
          resetChangeRequestParameters={true}
        />
      )}

      {tableDispatch.schemaSelections.map((selection) => (
        <TableDispatchSchemaValueSelect
          key={selection.fieldId}
          accountId={accountId}
          fieldName={selection.fieldName}
          schemaFieldId={selection.fieldId}
          value={selection.values}
          onChange={(values) => handleSchemaValueChange(selection.fieldId, values)}
        />
      ))}
    </Stack>
  );
}
